import { useState } from 'react'
import { Modal } from '@/components/Modal'
import { Field, TextField } from '@/components/Form'
import { DataState } from '@/components/Common'
import { useAuth } from '@/lib/auth'
import { useResource } from '@/lib/resource'
import type { Filter, MatchOp, Reference } from '@/lib/api'

/**
 * Filters, for either a worklist or a rule.
 *
 * One component for both because a filter is the same thing whichever owns it
 * — the owner only decides WHEN it applies. A worklist's filters apply
 * whenever it is served; a rule's apply only when that rule matched.
 *
 * The thing this editor has to get across is that a filter only ever REMOVES
 * rows, and that a filter which cannot be resolved is worse than no filter at
 * all: the daemon drops it, so the owner matches more rows than intended. That
 * is why an unresolvable attribute is shown as an error on the row rather than
 * being quietly tolerated.
 */
export function FilterEditor({
  ownerKind,
  ownerId,
  ownerName,
  reference,
  onClose,
}: {
  ownerKind: 'worklist' | 'rule'
  ownerId: number
  ownerName: string
  reference: Reference | null
  onClose: () => void
}) {
  const path = `/api/v1/filters?owner_kind=${ownerKind}&owner_id=${ownerId}`
  const filters = useResource<Filter>(path)
  const { can } = useAuth()
  const mayEdit = can('worklist_admin')

  const [attribute, setAttribute] = useState('')
  const [op, setOp] = useState<MatchOp>('eq')
  const [value, setValue] = useState('')

  const attributes = reference?.attributes ?? []
  const ops = reference?.match_ops ?? {}
  const tokens = reference?.tokens ?? {}
  const needsValue = op !== 'isnull' && op !== 'notnull'

  const add = async () => {
    const created = await filters.create({
      owner_kind: ownerKind,
      owner_id: ownerId,
      attribute,
      match_op: op,
      value: needsValue ? value : '',
      enabled: true,
    })
    if (created) {
      setAttribute('')
      setValue('')
      setOp('eq')
    }
  }

  return (
    <Modal
      title={`Filters on ${ownerKind} '${ownerName}'`}
      subtitle="Each one removes rows. Nothing here can widen a result."
      onClose={onClose}
      footer={
        <button className="btn btn-primary" onClick={onClose}>
          Done
        </button>
      }
    >
      {filters.saveError && <div className="notice notice-error">{filters.saveError}</div>}

      <DataState
        loading={filters.loading}
        error={filters.error}
        empty={(filters.data ?? []).length === 0}
        emptyMessage={
          ownerKind === 'worklist'
            ? 'No filters. This worklist is narrowed only by its date window and status filter.'
            : 'No filters. This rule serves its worklist unchanged.'
        }
      />

      {(filters.data ?? []).length > 0 && (
        <table className="ae-table">
          <thead>
            <tr>
              <th>Attribute</th>
              <th>Test</th>
              <th>Value</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(filters.data ?? []).map((filter) => (
              <tr
                key={filter.id}
                className={
                  !filter.resolvable ? 'row-error' : filter.enabled ? undefined : 'row-muted'
                }
              >
                <td className="mono">
                  {filter.attribute}
                  {!filter.resolvable && (
                    <div className="warn-text">
                      not a known attribute — the daemon DROPS this filter, so the{' '}
                      {ownerKind} matches more rows than intended
                    </div>
                  )}
                </td>
                <td>{filter.match_op}</td>
                <td className="mono">{filter.value || '—'}</td>
                <td className="actions">
                  {mayEdit && (
                    <>
                      <button
                        className="btn btn-small"
                        disabled={filters.saving}
                        onClick={() =>
                          void filters.update(filter.id, { enabled: !filter.enabled })
                        }
                      >
                        {filter.enabled ? 'Disable' : 'Enable'}
                      </button>
                      <button
                        className="btn btn-small btn-danger-quiet"
                        disabled={filters.saving}
                        onClick={() => void filters.remove(filter.id)}
                      >
                        Remove
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {mayEdit && (
        <>
          <div className="subhead">Add a filter</div>
          <div className="form-grid">
            <Field label="Attribute" required>
              <select value={attribute} onChange={(e) => setAttribute(e.target.value)}>
                <option value="">choose an attribute…</option>
                {attributes.map((a) => (
                  <option key={a.keyword} value={a.keyword}>
                    {a.keyword} {a.tag}
                    {a.in_sps ? ' · in step' : ''}
                  </option>
                ))}
              </select>
              <span className="field-hint">
                Only attributes this server understands. Anything else would be
                dropped at load.
              </span>
            </Field>

            <Field label="Test">
              <select value={op} onChange={(e) => setOp(e.target.value as MatchOp)}>
                {Object.entries(ops).map(([key, description]) => (
                  <option key={key} value={key}>
                    {key} — {description}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {needsValue && (
            <TextField
              label="Value"
              value={value}
              onChange={setValue}
              maxLength={400}
              hint={
                op === 'in' || op === 'notin'
                  ? 'Comma-separated, e.g. CT,PT'
                  : op === 'range'
                    ? 'low..high, inclusive. Either side may be empty.'
                    : op === 'like'
                      ? 'DICOM wildcards: * for any run, ? for one character.'
                      : 'May contain a $TOKEN — see below.'
              }
            />
          )}

          <details className="card-note">
            <summary>Tokens you can use in a value</summary>
            <p className="hint">
              Resolved per query, which is what lets one rule serve a whole
              fleet. A token that resolves to nothing makes the filter{' '}
              <strong>inert</strong> for that query — the caller sees a wider
              list, and the daemon logs it every time. That direction is
              deliberate: a blank worklist at 3am with no explanation is worse.
            </p>
            <table className="ae-table">
              <tbody>
                {Object.entries(tokens).map(([token, description]) => (
                  <tr key={token}>
                    <td className="mono">{token}</td>
                    <td>{description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>

          <div className="form-actions">
            <button
              className="btn btn-primary"
              disabled={filters.saving || !attribute || (needsValue && !value)}
              onClick={() => void add()}
            >
              {filters.saving ? 'Adding…' : 'Add filter'}
            </button>
          </div>
        </>
      )}
    </Modal>
  )
}
